import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FormControl, Stack, TextField, Select, MenuItem, InputLabel, Button, Box, Typography
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, UploadFile as UploadFileIcon, Add as AddIcon } from '@mui/icons-material';

import { Container, DataGrid } from '@/components/common';
import { useUserList, useUpdateUserStatus } from '@/hooks/users.hook';
import { successToast, errorToast } from '@/common/toast';
import { formatDate } from '@/util/dateUtils';
import { useGroupDetail } from '@/hooks/groups.hook';
import { DateCell, UserNameCell, UserTypeCell, UserStatusCell } from '../components';
import ExcelUploadModal from './components/ExcelUploadModal';

/**
 * 그룹에 소속된 사용자 목록 조회 페이지 컴포넌트
 * 
 * 특정 그룹에 소속된 사용자 목록을 조회하고 관리하는 페이지입니다.
 * 
 * 데이터 구조:
 * - groupId (int)
 * - userId (string)
 * - name(email) (string)
 * - type (string) [교육생, FT, 관리자, 교육운영자]
 * - expireDate (datetime)
 * - regDate (datetime)
 * - status (string) [active: 활성화, inactive: 비활성화, secession: 계정 잠금]
 */
const GroupUsersPage = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();

  // 그룹 정보 조회
  const { data: groupDetailData } = useGroupDetail(groupId);
  const groupData = groupDetailData?.groupInfo;

  // 엑셀 업로드 모달 상태
  const [excelUploadModalOpen, setExcelUploadModalOpen] = useState(false);

  // 검색 상태
  const [searchType, setSearchType] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');

  // 실제 쿼리용 상태
  const [queryOptions, setQueryOptions] = useState({
    type: 'all',
    search: ''
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // 상태 변경 훅
  const updateStatusMutation = useUpdateUserStatus();
  const { data, isLoading, refetch } = useUserList({
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    groupId: parseInt(groupId), // 그룹 ID로 필터링
    isStudent: true,
    searchType: queryOptions.type,
    keyword: queryOptions.search,
  });

  const total = data?.total ?? 0;
  const rowData = data?.list ?? [];

  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) setRowCountState(total);
  }, [total, isLoading]);

  // 상태 변경
  const handleStatusChange = useCallback((userId, newStatus, secessionOpinion) => {
    updateStatusMutation.mutate(
      { userId, status: newStatus, secessionOpinion },
      {
        onSuccess: () => successToast('회원 상태가 변경되었습니다.'),
        onError: () => errorToast('상태 변경 실패'),
      }
    );
  }, [updateStatusMutation]);

  // 검색 실행
  const handleSearch = useCallback(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setQueryOptions({
      type: searchType,
      search: searchKeyword,
    });
  }, [searchType, searchKeyword]);

  // 초기화
  const handleReset = useCallback(() => {
    setSearchType('all');
    setSearchKeyword('');
    setQueryOptions({ type: 'all', search: '' });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  // 행 클릭 핸들러
  const handleRowClick = useCallback((params) => {
    navigate(`/students/detail/${params.row.userId}`);
  }, [navigate]);

  // 셀 클릭 핸들러 (특정 셀에서는 행 클릭 방지)
  const handleCellClick = useCallback((params, event) => {
    // status 필드 셀 클릭 시 행 클릭 방지
    if (params.field === 'status') {
      event.stopPropagation();
    }
  }, []);

  // 컬럼 정의 (메모이제이션하여 불필요한 리렌더링 방지)
  // TODO: 접속 가능일
  const columns = useMemo(() => [
    { field: 'userId', headerName: '아이디', minWidth: 120, flex: 1 },
    {
      field: 'name',
      headerName: '이름 (이메일)',
      minWidth: 200,
      flex: 1.5,
      sortable: false,
      renderCell: (params) => <UserNameCell row={params.row} />,
    },
    {
      field: 'type',
      headerName: '권한',
      minWidth: 100,
      sortable: false,
      renderCell: (params) => <UserTypeCell row={params.row} />,
    },
    {
      field: 'regDate',
      headerName: '생성일',
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <DateCell row={params.row} dateField="createdAt" />,
    },
    {
      field: 'status',
      headerName: '상태',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <UserStatusCell 
          row={params.row} 
          onStatusChange={handleStatusChange}
        />
      ),
    }
  ], [handleStatusChange]);

  return (
    <Container title={`[${groupData?.groupName + ' ' + groupData?.groupCourse + ' ' + groupData?.groupCourseCount}차수] 교육생 목록`}>
      {/* 뒤로가기 버튼 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/groups')}
          sx={{ mb: 2 }}
        >
          그룹 목록으로
        </Button>
      </Stack>

      {/* 그룹 정보 표시 */}
      {groupData && (
        <Box mb={3} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            그룹 정보
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Typography variant="body2">
              <strong>고객사명:</strong> {groupData.groupName || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>그룹명:</strong> {groupData.groupCourse || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>시작일:</strong> {formatDate(groupData.groupStartDate) || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>종료일:</strong> {formatDate(groupData.groupDueDate) || '-'}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* 1. 상단 필터 및 버튼 영역 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        {/* 검색 필터 그룹 */}
        <Stack direction="row" gap={1} alignItems="center">
          <FormControl size='small' sx={{ width: 120 }}>
            <InputLabel>검색 조건</InputLabel>
            <Select
              value={searchType}
              label="검색 조건"
              onChange={(e) => setSearchType(e.target.value)}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='userId'>아이디</MenuItem>
              <MenuItem value='name'>이름</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size='small'
            placeholder='검색어 입력'
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 200 }}
          />

          <Button variant='contained' onClick={handleSearch} sx={{ height: 40 }}>검색</Button>
          <Button variant='outlined' onClick={handleReset} sx={{ height: 40 }}>초기화</Button>
        </Stack>

        {/* 우측 액션 버튼 그룹 */}
        <Stack direction="row" gap={1}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setExcelUploadModalOpen(true)}
          >
            엑셀 등록
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/students/new?groupId=${groupId}`)}
          >
            교육생 등록
          </Button>
        </Stack>
      </Stack>

      {/* 2. 데이터 그리드 */}
      <DataGrid
        rows={rowData}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        height={'auto'}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row) => row.userId}
        onRowClick={handleRowClick}
        onCellClick={handleCellClick}
      />

      {/* 엑셀 업로드 모달 */}
      <ExcelUploadModal
        open={excelUploadModalOpen}
        onClose={() => setExcelUploadModalOpen(false)}
        groupId={groupId}
        onSuccess={() => {
          refetch();
        }}
      />
    </Container>
  );
};

export default GroupUsersPage;
